/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AddCircle, Clear, Delete, Download, Edit, PictureAsPdf, Search, FileCopy } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import useThrowAsyncError from "@/hooks/useThrowAsyncError";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import { useAuth } from "@/context/global";
import IGenericResult from "@/interfaces/IGenericResult";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import DialogNew from "./modal/dialogAdd";
import DialogEdit from "./modal/dialogEdit";
import DialogReplicate from "./modal/dialogReplicate";
import messages from "@/utils/messages";
import ConfirmationDialog from "@/components/confirmationDialog";
import serviceModeloFatura from "@/services/serviceModeloFatura";
import { habilitadoOptions } from "@/utils/constants";

const tipoFaturaText = {
    C: "Concessionária",
    U: "Usina",
};

const habilitadoFilterOptions = { Todos: "", Sim: 1, Nao: 0, "": "" };

const CadastroModelo = () => {
    const { setTitlePage } = useAuth();
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [modeloDataOptions, setModeloDataOptions] = useState<IGenericResult[]>([]);
    const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
    const [exclusionLoading, setExclusionLoading] = useState(false);
    const [exclusionState, setExclusionState] = useState({ data: "", open: false });
    const [data, setData] = useState<any>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState({ habilitado: "Sim" });
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(10);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const tipoFaturaDataOptions = [
        { label: "Concessionária", value: "C" },
        { label: "Usina", value: "U" },
    ];

    useEffect(() => {
        setTitlePage("Captura de Faturas - Cadastro de Modelos");
    }, [setTitlePage]);

    const [pageSize, setPageSize] = useState(100);
    const [editState, setEditState] = useState({
        open: false,
        modeloFaturaId: 0,
        concessionariaID: 0,
        nomeConcessionaria: "",
        descricaoModelo: "",
        tipoFatura: "",
    });
    const [replicateState, setReplicateState] = useState({
        open: false,
        modeloFaturaId: 0,
        descricaoModelo: "",
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            concessionariaID: "",
            modeloFaturaID: "",
            tipoFatura: "",
            habilitado: "Sim",
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                width: 200,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Baixar Arquivo">
                                <PictureAsPdf />
                            </Tooltip>
                        }
                        label="baixarArquivo"
                        onClick={() => downloadModelo(params.row.modeloFaturaId, "C", params.row.arquivoTipo)}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Converter Arquivo">
                                <Download />
                            </Tooltip>
                        }
                        label="converterArquivo"
                        onClick={() => {
                            downloadModelo(params.row.modeloFaturaId, "M", params.row.arquivoTipo);
                        }}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Replicar Modelo">
                                <FileCopy />
                            </Tooltip>
                        }
                        label="Replicar Modelo"
                        onClick={() => {
                            setReplicateState({
                                open: true,
                                modeloFaturaId: params.row.modeloFaturaId,
                                descricaoModelo: params.row.descricaoModelo,
                            });
                        }}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar">
                                <Edit />
                            </Tooltip>
                        }
                        label="Editar"
                        onClick={() =>
                            setEditState({
                                open: true,
                                modeloFaturaId: params.row.modeloFaturaId,
                                concessionariaID: params.row.concessionariaID,
                                nomeConcessionaria: params.row.nomeConcessionaria,
                                descricaoModelo: params.row.descricaoModelo,
                                tipoFatura: params.row.tipoFatura,
                            })
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Desabilitar">
                                <Delete />
                            </Tooltip>
                        }
                        label="Desabilitar"
                        onClick={() => setExclusionState({ data: params.id.toString(), open: true })}
                        disabled={params.row.habilitado === 0}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "modeloFaturaId",
                type: "number",
                headerAlign: "left",
                identity: true,
                maxWidth: 110,
                flex: 1,
                id: 70,
            },
            {
                headerName: "Concessionária",
                field: "nomeConcessionaria",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "Descrição Modelo",
                field: "descricaoModelo",
                type: "string",
                minWidth: 350,
                flex: 1,
            },
            {
                headerName: "Modelo Vinculado",
                field: "modeloVinculado",
                type: "string",
                minWidth: 350,
                flex: 1,
            },
            {
                headerName: "Tipo de Fatura",
                field: "tipoFatura",
                type: "string",
                minWidth: 200,
                flex: 1,
                valueFormatter: (item: any) => {
                    return tipoFaturaText[item.value];
                },
            },
            {
                headerName: "Habilitado",
                field: "habilitado",
                type: "string",
                minWidth: 100,
                flex: 1,
                valueFormatter: (item: any) => {
                    return item.value === 1 ? "Sim" : "Não";
                },
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    useEffect(() => {
        setConcessionariaData();
        setModeloData();
    }, []);

    const downloadModelo = async (id: any, tipoDownload: string, arquivoTipo: string) => {
        setIsLoading(true);

        serviceModeloFatura
            .downloadModelo(id, tipoDownload, arquivoTipo)
            .catch((err) => {
                toast.error(messages.faturas.error, err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const setModeloData = async () => {
        serviceModeloFatura
            .getAllModelos()
            .then((result) => {
                setModeloDataOptions(
                    result?.data?.map((data) => {
                        return { value: data.modeloFaturaId, label: data.descricaoModelo };
                    })
                );
            })
            .catch(() => {
                setModeloDataOptions([]);
            });
    };

    const throwAsyncError = useThrowAsyncError();

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setIsLoading(true);

        if (!filters) {
            filters = { ativo: true };
        }

        serviceModeloFatura
            .getModelosWithFilter({
                ...filters,
                habilitado: habilitadoFilterOptions[filters.habilitado],
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data.content);
                setRowCount(result.data.numberOfElements);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDelete = async () => {
        setExclusionLoading(true);

        serviceModeloFatura
            .deleteModelo(exclusionState.data)
            .then((result) => {
                if (result) {
                    toast.success(messages.disableSucess);
                }
            })
            .catch((err) => {
                toast.error(messages.disableError, err);
            })
            .finally(() => {
                setExclusionLoading(false);
                setExclusionState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    const onClear = () => {
        reset({ concessionariaID: "", tipoFatura: "", habilitado: "", modeloFaturaID: "" });
        setFilters({ habilitado: "" });
        getAllWithFilters({ habilitado: "" });
    };

    const onSuccessAction = () => {
        setConcessionariaData();
        setModeloData();
        getAllWithFilters(filters);
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} mt={2}>
                                <Grid item md={12} xs={12}>
                                    <Grid container spacing={2} display="flex" alignItems="center">
                                        <Grid item md={4} xs={12}>
                                            <AutoComplete
                                                id="concessionariaID"
                                                name="concessionariaID"
                                                label="Concessionária"
                                                options={concessionariaDataOptions ?? []}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <AutoComplete
                                                id="modeloFaturaID"
                                                name="modeloFaturaID"
                                                label="Descrição Modelo"
                                                options={modeloDataOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="pesquisar"
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="novo"
                                                variant="contained"
                                                color="primary"
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => setOpenModalAdd(true)}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Novo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item md={4} xs={12}>
                                    <AutoComplete
                                        id="tipoFatura"
                                        name="tipoFatura"
                                        label="Tipo de Fatura"
                                        options={tipoFaturaDataOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item md={4} xs={12}>
                                    <AutoComplete
                                        id="habilitado"
                                        name="habilitado"
                                        label="Habilitado"
                                        options={habilitadoOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button
                                        id="limpar"
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<Clear />}
                                        onClick={() => onClear()}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Limpar
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data ?? []}
                            rowId={(row: GridRowModel) => row.modeloFaturaId}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                            disableRowSelectionOnClick={true}
                        />
                    </Box>
                </CardContent>
            </Card>
            <DialogNew
                open={openModalAdd}
                concessionariaDataOptions={concessionariaDataOptions}
                tipoFaturaDataOptions={tipoFaturaDataOptions}
                modeloDataOptions={modeloDataOptions}
                onSuccess={() => onSuccessAction()}
                onClose={() => setOpenModalAdd(false)}
            />

            <DialogEdit
                open={editState.open}
                concessionariaDataOptions={concessionariaDataOptions}
                tipoFaturaDataOptions={tipoFaturaDataOptions}
                onSuccess={() => onSuccessAction()}
                data={{
                    open: editState.open,
                    modeloFaturaId: editState.modeloFaturaId,
                    concessionariaID: editState.concessionariaID,
                    nomeConcessionaria: editState.nomeConcessionaria,
                    descricaoModelo: editState.descricaoModelo,
                    tipoFatura: editState.tipoFatura,
                }}
                onClose={() =>
                    setEditState({
                        open: false,
                        modeloFaturaId: 0,
                        concessionariaID: 0,
                        nomeConcessionaria: "",
                        descricaoModelo: "",
                        tipoFatura: "",
                    })
                }
            />

            <DialogReplicate
                open={replicateState.open}
                modeloDataOptions={modeloDataOptions}
                onSuccess={() => onSuccessAction()}
                data={{
                    open: replicateState.open,
                    modeloFaturaId: replicateState.modeloFaturaId,
                    descricaoModelo: replicateState.descricaoModelo,
                }}
                onClose={() =>
                    setReplicateState({
                        open: false,
                        modeloFaturaId: 0,
                        descricaoModelo: "",
                    })
                }
            />

            <ConfirmationDialog
                open={exclusionState?.open}
                loading={exclusionLoading}
                title={"Desabilitar Modelo"}
                message={"Deseja realmente desabilitar o modelo selecionado?"}
                cancelButtonText={"Não"}
                confirmButtonText={"Sim"}
                onClose={() => setExclusionState({ data: "", open: false })}
                onCancel={() => setExclusionState({ data: "", open: false })}
                onConfirm={() => {
                    handleDelete();
                }}
            />
        </Container>
    );
};

export default CadastroModelo;
